import { useContext, useEffect, useMemo, useRef } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import { ChatStreamStatusContext } from 'tripPlanner/assistant/contexts/ChatStreamStatusContext'
import { useAppSelector } from 'hooks/reduxHooks'
import { getLegacyTripArchives } from './api'
import { legacyArchiveKeys } from './queryKeys'

export interface ClassicTripHistoryItem {
  id: string
  name: string
  updatedAt: string
  subtitle?: string
  canManage?: boolean
}

// Classic history reads only the archive; current chat does not consume the
// retired itinerary model.
export default function useClassicTripHistory({ enabled }: { enabled: boolean }) {
  const queryClient = useQueryClient()
  // The landing page also uses this hook, outside the streaming provider.
  const isStreamOpen = useContext(ChatStreamStatusContext)?.isStreamOpen ?? false
  const wasStreamOpen = useRef(isStreamOpen)
  const memberId = useAppSelector(state => state.auth.account.memberId ?? '')
  const archives = useQuery({
    queryKey: legacyArchiveKeys.list(memberId),
    queryFn: () => getLegacyTripArchives(),
    enabled: enabled && !!memberId,
    cacheTime: 0,
  })
  useEffect(() => {
    // content_complete/message_end can precede backend migration tracking.
    // Refresh only when the protocol stream closes, after tracking has finished.
    if (wasStreamOpen.current && !isStreamOpen && enabled && memberId) {
      queryClient.invalidateQueries({ queryKey: legacyArchiveKeys.list(memberId) })
    }
    wasStreamOpen.current = isStreamOpen
  }, [enabled, isStreamOpen, memberId, queryClient])

  const data = useMemo<Array<ClassicTripHistoryItem>>(() =>
    (archives.data ?? []).map(archive => ({
      id: archive.id,
      name: archive.name,
      updatedAt: archive.updatedAt,
      canManage: false,
    })), [archives.data])

  return {
    data,
    isLoading: archives.isInitialLoading,
    isError: archives.isError,
    refetch: archives.refetch,
  }
}
